import { useState, useEffect, useCallback } from 'react'
import { MessageSquare, Plus, User, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { PublicationNote } from '@/types/publication'
import { PublicationService } from '@/services/publicationService'
import { parseDbTimestamp } from '@/utils/datetime'

interface PublicationNotesProps {
  publicationId: number
  className?: string
}

export function PublicationNotes({ publicationId, className }: PublicationNotesProps) {
  const [notes, setNotes] = useState<PublicationNote[]>([])
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [newNoteText, setNewNoteText] = useState('')
  const [loading, setLoading] = useState(false)

  const loadNotes = useCallback(async () => {
    try {
      const response = await PublicationService.getNotes(publicationId)
      if (response.success) {
        setNotes(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar notas:', error)
    }
  }, [publicationId])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return

    try {
      setLoading(true)
      await PublicationService.addNote(publicationId, newNoteText.trim())
      setNewNoteText('')
      setIsAddingNote(false)
      await loadNotes()
    } catch (error) {
      console.error('Erro ao adicionar nota:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      const date = parseDbTimestamp(dateString)
      if (!date) return dateString
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className={`bg-card rounded-lg border ${className}`}>
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium text-foreground">
            Notas ({notes.length})
          </h3>
        </div>
        
        {!isAddingNote && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddingNote(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar Nota
          </Button>
        )}
      </div>

      <div className="p-4">
        {isAddingNote && (
          <div className="mb-4 space-y-3">
            <Textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Digite sua nota..."
              className="resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAddingNote(false)
                  setNewNoteText('')
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={loading || !newNoteText.trim()}
              >
                {loading ? 'Salvando...' : 'Salvar Nota'}
              </Button>
            </div>
          </div>
        )}

        {notes.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p>Nenhuma nota adicionada</p>
            <p className="text-sm">Use o botão &quot;Adicionar Nota&quot; para começar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="bg-muted/40 rounded-lg p-3 border"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>{[note.user?.firstName, note.user?.lastName].filter(Boolean).join(' ') || note.createdBy || 'Usuário'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(note.createdAt)}</span>
                  </div>
                </div>
                <p className="text-foreground text-sm whitespace-pre-wrap">
                  {note.noteText}
                </p>
                {note.noteType && note.noteType !== 'USER' && (
                  <div className="mt-2">
                    <Badge variant="secondary">{note.noteType}</Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}